import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import GirviPageShell from '../../common/GirviPageShell';

const BillingSyncSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    billingUrl: '',
    syncApiKey: '',
    billingEmail: ''
  });

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/billing-sync/integration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.status === 'success' && res.data.data) {
        const d = res.data.data;
        setForm({
          enabled: !!d.enabled,
          billingUrl: d.billingUrl || '',
          syncApiKey: d.syncApiKey || '',
          billingEmail: d.billingEmail || ''
        });
      }
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load billing sync settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (field) => (e) => {
    const value = field === 'enabled' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.enabled && (!form.billingUrl.trim() || !form.syncApiKey.trim() || !form.billingEmail.trim())) {
      setError('Billing URL, Sync API Key, and Billing Email are required when sync is enabled');
      return;
    }
    setSaveLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${baseURL}/billing-sync/integration`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.data?.status === 'success') {
        setSaveSuccess(true);
        if (res.data.data) {
          setForm({
            enabled: !!res.data.data.enabled,
            billingUrl: res.data.data.billingUrl || '',
            syncApiKey: res.data.data.syncApiKey || '',
            billingEmail: res.data.data.billingEmail || ''
          });
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <GirviPageShell
      title="Billing / Proforma Sync"
      subtitle="Master"
      chipLabel="SYNC"
      loading={loading}
     compact>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          When enabled, new customers created on VPS B are sent to the linked billing shop (legal + proforma).
          Enter the billing login email — it uniquely identifies which shop to sync to.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSave}>
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={handleChange('enabled')} />}
              label="Enable sync to Billing / Proforma"
              sx={{ mb: 2, display: 'block' }}
            />
            <TextField
              fullWidth
              label="Billing backend URL"
              placeholder="http://localhost:5044"
              value={form.billingUrl}
              onChange={handleChange('billingUrl')}
              margin="normal"
              helperText="Billing API base URL only — not the billing website port"
            />
            <TextField
              fullWidth
              label="Sync API Key"
              type="password"
              value={form.syncApiKey}
              onChange={handleChange('syncApiKey')}
              margin="normal"
              helperText="Must match the same key saved in that shop's proforma Girvi Sync settings"
            />
            <TextField
              fullWidth
              label="Billing login email"
              type="email"
              placeholder="manish@mail.com"
              value={form.billingEmail}
              onChange={handleChange('billingEmail')}
              margin="normal"
              required
              helperText="Unique billing user email (e.g. manish@mail.com). That user must have Girvi Sync enabled in proforma."
            />
            <Box sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" disabled={saveLoading}>
                {saveLoading ? 'Saving…' : 'Save Settings'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mt: 3 }}>
          On proforma: that user logs in → Settings → Girvi Sync ON with the same API key. VPS B uses email to know which shop receives customers.
        </Alert>

      <Snackbar open={saveSuccess} autoHideDuration={4000} onClose={() => setSaveSuccess(false)}>
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Billing sync settings saved
        </Alert>
      </Snackbar>
    </GirviPageShell>
  );
};

export default BillingSyncSettings;
